import { useGSAP } from '@gsap/react';
import { ListIcon } from '@phosphor-icons/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppServices } from '@/app/AppServicesContext';
import { useContent } from '@/content/ContentContext';
import { MobileMenu } from '@/features/navigation/MobileMenu';
import { OxMark } from '@/ui/Brand/OxMark';
import { Wordmark } from '@/ui/Brand/Wordmark';
import { ButtonLink } from '@/ui/Button/Button';

import styles from './SiteHeader.module.css';

const MENU_ID = 'site-menu';

export function SiteHeader() {
  const { navigation } = useContent();
  const { motion } = useAppServices();
  const headerRef = useRef<HTMLElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useGSAP(() => {
    ScrollTrigger.create({
      start: 80,
      end: 'max',
      onToggle: (self) => {
        headerRef.current?.toggleAttribute('data-scrolled', self.isActive);
      },
    });
  });

  useEffect(() => {
    if (menuOpen) motion.lock();
    else motion.unlock();
  }, [menuOpen, motion]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  return (
    <header ref={headerRef} className={styles.header}>
      <div className={styles.bar}>
        <a className={styles.brand} href="#top" aria-label={navigation.homeLabel}>
          <OxMark className={styles.mark} />
          <Wordmark className={styles.wordmark} />
        </a>

        <nav className={styles.nav} aria-label="Navigation principale">
          {navigation.links.map((link) => (
            <a key={link.href} className={styles.link} href={link.href}>
              {link.label}
            </a>
          ))}
        </nav>

        <div className={styles.end}>
          <ButtonLink
            className={styles.cta}
            variant="outline"
            icon="arrow-right"
            href={navigation.cta.href}
          >
            {navigation.cta.label}
          </ButtonLink>
          <button
            type="button"
            className={styles.menuButton}
            aria-label={navigation.openMenu}
            aria-expanded={menuOpen}
            aria-controls={MENU_ID}
            onClick={() => {
              setMenuOpen(true);
            }}
          >
            <ListIcon size={20} aria-hidden="true" />
          </button>
        </div>
      </div>
      <MobileMenu id={MENU_ID} open={menuOpen} onClose={closeMenu} />
    </header>
  );
}
